import { Field } from '@atlaskit/form';
import Select, { OptionProps, ValueType } from '@atlaskit/select';
import React from 'react';
import { Order } from '@css-xp/graphtypes';
import { OrderLineItem } from '@css-xp/graphtypes/src/generated-types-new';
import { getDescriptor, getOrderLineItemStatusAndType } from '../../entitlement-utils';
import { FormattedDate } from '../../formatted';

export interface OrderSelectLineItem {
  value: string;
  order: Order;
}

export enum OrderType {
  CANCELLATION_ORDER,
  SYSTEM_CANCELLATION_ORDER,
}

const OrderItemSelectOption = ({
  data,
  getStyles,
  innerProps,
  innerRef,
  ...props
}: OptionProps<OrderSelectLineItem>) => {
  const { order } = data as { order: Order };
  const optionStyles = getStyles('option', props);
  const orderLineItemMessage =
    order &&
    order.orderLineItems &&
    order.orderLineItems.nodes.map((lineItem, index) => {
      const orderLineItem = ` 
            ${getOrderLineItemStatusAndType(lineItem?.status, lineItem?.type)} 
            ${lineItem?.parentEntitlementId ? 'add-on ' : ''} 
            ${getDescriptor(lineItem as OrderLineItem)}
        `;

      return <div key={index}>{orderLineItem}</div>;
    });

  return (
    <section ref={innerRef} {...innerProps} style={optionStyles} key={`select-option-${order.orderNumber}`}>
      <span data-testid={`select-option-${order.orderNumber}`}>
        <strong>
          <FormattedDate date={order.created} showTime />
        </strong>
        {orderLineItemMessage}
      </span>
    </section>
  );
};

interface Props {
  fieldName?: string;
  fieldLabel?: string;
  isRequired?: boolean;
  orderLineItems?: Order[] | null;
  onChange?: (orderItem: OrderSelectLineItem) => void;
  isSearchable?: boolean | false;
  billingFrequencyOnEntitlement?: string;
}

interface Option {
  label: string;
  value: string;
  data: any;
}

export const SelectOrderFormField = ({
  fieldName = 'order',
  fieldLabel = 'Select Order',
  isRequired = false,
  orderLineItems,
  onChange,
  isSearchable,
  billingFrequencyOnEntitlement,
}: Props) => {
  const orderSelectItems = (orderLineItems ?? [])
    .filter((order) => {
      if (billingFrequencyOnEntitlement) {
        order?.orderLineItems?.nodes?.[0]?.pricingPlan?.billingFrequency === billingFrequencyOnEntitlement;
      }
      return true;
    })
    .map((order) => {
      return {
        order,
        value: order.orderNumber,
      } as OrderSelectLineItem;
    });

  const onSelectInputChange = (option: Option, selectValue: string): boolean => {
    if (
      option.data.order &&
      option.data.order.orderLineItems &&
      option.data.order.orderLineItems.nodes &&
      option.data.order.orderLineItems.nodes[0] &&
      option.data.order.orderLineItems.nodes[0].pricingPlan
    ) {
      return (
        option.data.order.orderLineItems.nodes[0].pricingPlan.type.toLowerCase().indexOf(selectValue.toLowerCase()) > -1
      );
    }
    return false;
  };

  return (
    <div style={{ maxWidth: '500px' }}>
      <Field<ValueType<OrderSelectLineItem>, HTMLSelectElement>
        name={fieldName}
        label={fieldLabel}
        isRequired={isRequired}
      >
        {({ fieldProps }) => (
          <div data-testid="order-select-dropdown">
            <Select<OrderSelectLineItem>
              {...fieldProps}
              options={orderSelectItems}
              isDisabled={orderSelectItems.length === 0}
              components={{
                Option: OrderItemSelectOption,
                //@ts-ignore
                SingleValue: OrderItemSelectOption,
              }}
              isSearchable={isSearchable}
              onChange={(selectedItem) => {
                if (selectedItem && selectedItem.value) {
                  onChange && onChange(selectedItem);
                  fieldProps.onChange(selectedItem);
                }
              }}
              filterOption={onSelectInputChange}
            />
          </div>
        )}
      </Field>
    </div>
  );
};
